import { useCallback } from 'react';
import useSWR from 'swr';
import axiosInstance from '@/lib/axios';

export interface Device {
  id: string;
  deviceType: string;
  isActive: boolean;
  notificationsEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

const fetcher = (url: string) => axiosInstance.get(url).then((res) => res.data);

/**
 * Register device token for push notifications.
 */
export async function registerDeviceToken(
  deviceToken: string,
  deviceType: 'IOS' | 'ANDROID'
): Promise<void> {
  console.log('[FCM] Sending device token to backend...', {
    deviceToken: deviceToken.substring(0, 20) + '...',
    deviceType,
    timestamp: new Date().toISOString(),
  });

  const response = await axiosInstance.post('/api/devices', {
    deviceToken,
    deviceType,
  });

  console.log('[FCM] Device token registered successfully', {
    status: response.status,
    data: response.data,
  });
}

/**
 * Remove device token (unregister).
 */
export async function removeDeviceToken(deviceId: string): Promise<void> {
  await axiosInstance.delete(`/api/devices/${deviceId}`);
}

/**
 * Hook to get user's registered devices.
 */
export function useDevices() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const { data, error, isLoading, mutate } = useSWR<{ devices: Device[] }>(
    token ? '/api/devices' : null,
    fetcher,
    { revalidateOnFocus: false }
  );

  return {
    devices: data?.devices || [],
    isLoading,
    isError: error,
    mutate,
  };
}

/**
 * Hook to manage device token registration.
 * Call registerDeviceToken after getting the FCM token from Firebase.
 */
export function useDeviceRegistration() {
  const register = useCallback(async (deviceToken: string, deviceType: 'IOS' | 'ANDROID') => {
    try {
      await registerDeviceToken(deviceToken, deviceType);
      console.log('Device registered for push notifications');
    } catch (error) {
      console.error('Failed to register device:', error);
      throw error;
    }
  }, []);

  const unregister = useCallback(async (deviceId: string) => {
    try {
      await removeDeviceToken(deviceId);
      console.log('Device unregistered from push notifications');
    } catch (error) {
      console.error('Failed to unregister device:', error);
      throw error;
    }
  }, []);

  return { register, unregister };
}
